export interface DashboardSession {
  employeeId: string;
  expiresAt: string;
  email: string;
  name: string | null;
  picture: string | null;
  isAdmin: boolean;
}

export interface DashboardSessionResponse {
  token: string;
  session: DashboardSession;
}

export interface DailyActiveEmployeesRow {
  date: string;
  activeEmployees: number;
}

export interface TopItemRow {
  name: string;
  invocations: number;
  uniqueEmployees?: number;
  uniqueSessions?: number;
}

export type TopSkillRow = TopItemRow;

export type TopAgentRow = TopItemRow;

export type TopMcpToolRow = TopItemRow & {
  mcpServer?: string;
  mcpTool?: string;
};

export interface TokenUsageByEmployeeRow {
  employeeId: string;
  inputTokens: number;
  outputTokens: number;
  totalCostUsd: number;
  sessions?: number;
  events?: number;
  lastSeenAt?: string | null;
  roles?: string[];
  pluginVersion?: string | null;
}

export interface DashboardOverviewResponse {
  rangeStart: string;
  rangeEnd: string;
  dailyActiveEmployees: DailyActiveEmployeesRow[];
  topSkills: TopSkillRow[];
  topAgents: TopAgentRow[];
  topMcpTools: TopMcpToolRow[];
  tokenUsageByEmployee: TokenUsageByEmployeeRow[];
}

export interface DashboardCostGovernanceSummary {
  totalCostUsd: number;
  inputTokens: number;
  outputTokens: number;
  activeEmployees: number;
  sessions: number;
  highCostLowMcpSessions: number;
}

export interface DashboardCostGovernanceDailyCost {
  date: string;
  inputTokens: number;
  outputTokens: number;
  totalCostUsd: number;
  activeEmployees: number;
  sessions: number;
  previousDayCostUsd: number | null;
  costGrowthPct: number | null;
  isSpike: boolean;
}

export interface DashboardCostGovernanceBreakdown {
  name: string;
  inputTokens: number;
  outputTokens: number;
  totalCostUsd: number;
  activeEmployees: number;
  sessions: number;
}

export type DashboardCostGovernanceRoleCost = DashboardCostGovernanceBreakdown & {
  role: string;
};

export type DashboardCostGovernanceModelCost = DashboardCostGovernanceBreakdown & {
  model: string;
};

export interface DashboardCostGovernanceEmployeeCost {
  employeeId: string;
  roles: string[];
  inputTokens: number;
  outputTokens: number;
  totalCostUsd: number;
  sessions: number;
  events: number;
  mcpEvents: number;
  lastSeenAt: string | null;
}

export interface DashboardCostGovernanceSessionCandidate {
  sessionId: string;
  employeeId: string;
  startedAt: string;
  roles: string[];
  model: string;
  inputTokens: number;
  outputTokens: number;
  totalCostUsd: number;
  totalEvents: number;
  mcpEvents: number;
  reason: string;
}

export interface DashboardCostGovernanceResponse {
  windowDays: number;
  rangeStart: string;
  rangeEnd: string;
  limit: number;
  summary: DashboardCostGovernanceSummary;
  dailyCost: DashboardCostGovernanceDailyCost[];
  roleCost: DashboardCostGovernanceRoleCost[];
  modelCost: DashboardCostGovernanceModelCost[];
  employeeCost: DashboardCostGovernanceEmployeeCost[];
  highCostLowMcpSessions: DashboardCostGovernanceSessionCandidate[];
}

export type DashboardRolloutMcpStatus = 'configured' | 'used' | 'missing';

export type DashboardRolloutSetupStatus = 'completed' | 'failed' | 'unknown';

export type DashboardRolloutTelemetryStatus = 'healthy' | 'quiet' | 'silent';

export type DashboardRolloutUpdateStatus = 'applied' | 'available' | 'failed' | 'checked' | 'unknown';

export type DashboardRolloutHealthSortBy = 'lastSeen' | 'pluginVersion' | 'role' | 'stale' | 'error';

export interface DashboardRolloutHealthFilters {
  role?: string;
  pluginVersion?: string;
  mcpStatus?: DashboardRolloutMcpStatus | '';
  sortBy?: DashboardRolloutHealthSortBy;
  limit?: number;
  offset?: number;
}

export interface DashboardRolloutHealthSummary {
  installedEmployees: number;
  activeEmployees: number;
  telemetryHealthyEmployees: number;
  noRecentTelemetryEmployees: number;
  latestObservedPluginVersion: string | null;
  latestVersionEmployees: number;
  staleVersionEmployees: number;
  unknownVersionEmployees: number;
  setupCompletedEmployees: number;
  setupFailedEmployees: number;
  mcpConfiguredEmployees: number;
  mcpUsedEmployees: number;
  updateAvailableEmployees: number;
  updateFailedEmployees: number;
}

export interface DashboardRolloutHealthVersionAdoption {
  pluginVersion: string | null;
  employeeCount: number;
  activeEmployeeCount: number;
  sessionCount: number;
  lastSeenAt: string | null;
  isLatestObserved: boolean;
}

export interface DashboardRolloutHealthEmployee {
  employeeId: string;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  telemetryStatus: DashboardRolloutTelemetryStatus;
  sessions: number;
  events: number;
  roles: string[];
  pluginVersion: string | null;
  isLatestObservedVersion: boolean;
  isStalePlugin: boolean;
  mcpStatus: DashboardRolloutMcpStatus;
  mcpServers: string[];
  selectedMcpServers: string[];
  ontologyConfigured: boolean;
  mcpUsedAt: string | null;
  setupStatus: DashboardRolloutSetupStatus;
  setupCompletedAt: string | null;
  setupFailedAt: string | null;
  updateStatus: DashboardRolloutUpdateStatus;
  updateCheckedAt: string | null;
  updateAvailableAt: string | null;
  updateAppliedAt: string | null;
  updateFailedAt: string | null;
  mcpConfiguredAt: string | null;
  mcpFailedAt: string | null;
  doctorCompletedAt: string | null;
  doctorFailedAt: string | null;
  lastError: string | null;
  lastErrorAt: string | null;
}

export interface DashboardRolloutHealthResponse {
  windowDays: number;
  rangeStart: string;
  rangeEnd: string;
  limit: number;
  offset: number;
  total: number;
  filters: {
    role: string | null;
    pluginVersion: string | null;
    mcpStatus: DashboardRolloutMcpStatus | null;
    sortBy: DashboardRolloutHealthSortBy;
  };
  summary: DashboardRolloutHealthSummary;
  versionAdoption: DashboardRolloutHealthVersionAdoption[];
  employees: DashboardRolloutHealthEmployee[];
}

export type DashboardTopListKind = 'skills' | 'agents' | 'mcp-tools' | 'employees';

export type DashboardTopListItem = TopItemRow | TokenUsageByEmployeeRow;

interface DashboardTopListBaseResponse {
  windowDays: number;
  rangeStart: string;
  rangeEnd: string;
  limit: number;
  offset: number;
  total: number;
}

export type DashboardTopListResponse =
  | (DashboardTopListBaseResponse & { kind: 'skills'; items: TopSkillRow[] })
  | (DashboardTopListBaseResponse & { kind: 'agents'; items: TopAgentRow[] })
  | (DashboardTopListBaseResponse & { kind: 'mcp-tools'; items: TopMcpToolRow[] })
  | (DashboardTopListBaseResponse & { kind: 'employees'; items: TokenUsageByEmployeeRow[] });

export interface EmployeeSummaryResponse {
  employeeId: string;
  windowDays: number;
  rangeStart: string;
  rangeEnd: string;
  sessions: number;
  events: number;
  inputTokens: number;
  outputTokens: number;
  totalCostUsd: number;
  topSkills: TopSkillRow[];
  topAgents: TopAgentRow[];
  topMcpTools: TopMcpToolRow[];
}

export interface EmployeeSessionRow {
  sessionId: string;
  employeeId: string;
  startedAt: string;
  endedAt: string | null;
  endedNormally: boolean | null;
  totalEvents: number | null;
  totalInputTokens: number | null;
  totalOutputTokens: number | null;
  totalCostUsd: number | null;
  hostname: string | null;
  cwd: string | null;
  gitRepo: string | null;
  pluginVersion: string | null;
  claudeCodeVersion: string | null;
}

export interface EmployeeSessionsResponse {
  employeeId: string;
  windowDays: number;
  rangeStart: string;
  rangeEnd: string;
  sessions: EmployeeSessionRow[];
}

export interface SessionEventRow {
  eventId: string;
  sessionId: string;
  employeeId: string;
  occurredAt: string;
  eventType: string;
  toolName: string | null;
  skillName: string | null;
  agentSubagentType: string | null;
  mcpServer: string | null;
  mcpTool: string | null;
  durationMs: number | null;
  success: boolean | null;
}

export interface SessionEventsResponse {
  sessionId: string;
  events: SessionEventRow[];
}

export type SkillProposalStatus =
  | 'pending_review'
  | 'approved'
  | 'rejected'
  | 'revision_requested'
  | 'converted_to_shared'
  | 'delivered';

export type SkillProposalReviewAction = 'approve' | 'reject' | 'request_revision' | 'convert_to_shared';

export type SkillProposalScope = 'personal' | 'role' | 'multi_role' | 'company_wide';

export type AgentRoleAssetKind = 'skill' | 'agent' | 'hook' | 'instruction' | 'mcp_preset';

export type AgentRoleAssetScope = 'role' | 'multi_role' | 'company_wide';

export type AgentRoleAssetStatus = 'draft' | 'active' | 'retired';

export interface AgentRoleAssetMetricsRow {
  assetId: string;
  windowDays: number;
  rangeStart: string;
  rangeEnd: string;
  eligibleEmployeeCount: number;
  appliedEmployeeCount: number;
  activeEmployeeCount: number;
  appliedCount: number;
  skippedCount: number;
  failedCount: number;
  usageCount: number;
  impactedSessionCount: number;
  impactedInputTokens: number;
  impactedOutputTokens: number;
  impactedCostUsd: number;
  lastAppliedAt: string | null;
  lastUsedAt: string | null;
}

export interface AgentRoleAssetRow {
  assetId: string;
  kind: AgentRoleAssetKind;
  adapter: string;
  title: string;
  description: string;
  scope: AgentRoleAssetScope;
  roles: string[];
  status: AgentRoleAssetStatus;
  activeVersion: number | null;
  latestVersion: number;
  contentHash: string | null;
  sourceProposalId: string | null;
  sourceProposalAssetId: string | null;
  ownerEmployeeId: string;
  reviewerEmployeeId: string | null;
  reviewNote: string | null;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
  retiredAt: string | null;
  metrics: AgentRoleAssetMetricsRow;
}

export interface AgentRoleAssetVersionRow {
  assetId: string;
  version: number;
  kind: AgentRoleAssetKind;
  adapter: string;
  title: string;
  description: string;
  scope: AgentRoleAssetScope;
  roles: string[];
  contentMarkdown: string;
  contentHash: string;
  targetPath: string;
  status: AgentRoleAssetStatus;
  sourceProposalId: string | null;
  sourceProposalAssetId: string | null;
  createdByEmployeeId: string;
  reviewerEmployeeId: string | null;
  reviewNote: string | null;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
  retiredAt: string | null;
  bundleRootHash?: string | null;
  bundleFiles?: SkillBundleFile[];
  bundle?: SkillBundle;
  adapterBundles?: SkillAdapterBundle[];
  sourceRoleAssetId?: string | null;
  baseVersion?: number | null;
  baseBundleHash?: string | null;
}

export interface AgentRoleAssetListFilters {
  kind?: AgentRoleAssetKind | '';
  scope?: AgentRoleAssetScope | '';
  status?: AgentRoleAssetStatus | '';
  role?: string;
  adapter?: string;
  limit?: number;
}

export interface AgentRoleAssetsResponse {
  assets: AgentRoleAssetRow[];
}

export interface AgentRoleAssetDetailResponse {
  asset: AgentRoleAssetRow;
  versions: AgentRoleAssetVersionRow[];
}

export interface AgentRoleAssetTransitionRequest {
  assetId: string;
  note?: string;
}

export interface AgentRoleAssetRollbackRequest extends AgentRoleAssetTransitionRequest {
  version: number;
}

export type AgentAssetEvolutionAction = 'investigate_failures' | 'review_instructions' | 'improve_onboarding';

export type AgentAssetEvolutionRisk = 'low' | 'medium' | 'high';

export interface AgentAssetEvolutionCandidateMetrics {
  eligibleEmployees: number;
  adoptedEmployees: number;
  activeEmployees: number;
  invocations: number;
  errors: number;
  skipped: number;
  impactedSessions: number;
  impactedInputTokens: number;
  impactedOutputTokens: number;
  impactedCostUsd: number;
  errorRate: number;
  lastAppliedAt: string | null;
  lastUsedAt: string | null;
}

export interface AgentAssetEvolutionCandidate {
  assetId: string;
  title: string;
  description: string;
  kind: AgentRoleAssetKind;
  adapter: string;
  scope: AgentRoleAssetScope;
  roles: string[];
  status: AgentRoleAssetStatus;
  activeVersion: number | null;
  latestVersion: number;
  recommendedAction: AgentAssetEvolutionAction;
  recommendedActionLabel: string;
  suggestedNextStep: string;
  riskLevel: AgentAssetEvolutionRisk;
  evidenceSummary: string;
  humanReviewRequired: boolean;
  metrics: AgentAssetEvolutionCandidateMetrics;
  updatedAt: string;
}

export interface AgentAssetEvolutionCandidatesResponse {
  windowDays: number;
  generatedAt: string;
  candidates: AgentAssetEvolutionCandidate[];
}

export interface SkillProposalReviewRequest {
  proposalId: string;
  action: SkillProposalReviewAction;
  note?: string;
  finalScope?: SkillProposalScope;
  selectedRoles?: string[];
}

export type SkillProposalSource = 'telemetry_batch' | 'local_submission';

export interface SkillProposalRow {
  proposalId: string;
  employeeId: string | null;
  role: string | null;
  proposalType: string;
  proposalSource?: SkillProposalSource;
  status: SkillProposalStatus;
  title: string;
  summary: string;
  evidenceSummary: string;
  recommendedAction: string;
  riskLevel: 'low' | 'medium' | 'high';
  confidence: number;
  targetAdapter: string;
  targetSkillId: string | null;
  targetSkillName: string | null;
  targetPathCategory: string | null;
  sourceEventCount: number;
  sourceSessionCount: number;
  createdAt: string;
  updatedAt: string;
  reviewedAt: string | null;
  reviewerEmployeeId: string | null;
  reviewNote: string | null;
  deliveryVersion: string | null;
  sourceRoleAssetId?: string | null;
  baseVersion?: number | null;
  baseBundleHash?: string | null;
  riskFindings?: SkillRiskFinding[];
}

export interface SkillProposalAssetRow {
  assetId: string;
  proposalId: string;
  adapter: string;
  skillId: string;
  skillName: string;
  contentMarkdown: string;
  contentHash: string;
  targetPath: string;
  status: string;
  updatedAt: string;
  bundle?: SkillBundle;
  bundleRootHash?: string | null;
}

/**
 * A bundle is reviewed as source text only. The API retains the legacy single-file fields above
 * until every client has negotiated bundle delivery support.
 */
export interface SkillBundle {
  rootHash: string;
  files: SkillBundleFile[];
}

export interface SkillBundleFile {
  path: string;
  adapter?: string;
  mediaType: string;
  byteSize: number;
  contentHash: string;
  content: string;
}

export interface SkillAdapterBundle extends SkillBundle {
  adapter: string;
}

export interface SkillBundleReviewAsset {
  assetId: string;
  adapter: string;
  contentMarkdown: string;
  contentHash: string;
  targetPath: string;
  bundle?: SkillBundle;
}

export const roleVersionReviewAssets = (version: AgentRoleAssetVersionRow): SkillBundleReviewAsset[] => {
  if (version.adapterBundles?.length) {
    return version.adapterBundles.map((bundle) => ({
      assetId: `${version.assetId}:${version.version}:${bundle.adapter}`,
      adapter: bundle.adapter,
      contentMarkdown: version.contentMarkdown,
      contentHash: bundle.rootHash,
      targetPath: version.targetPath,
      bundle,
    }));
  }
  if (version.bundle) {
    return [
      {
        assetId: `${version.assetId}:${version.version}`,
        adapter: version.adapter,
        contentMarkdown: version.contentMarkdown,
        contentHash: version.contentHash,
        targetPath: version.targetPath,
        bundle: version.bundle,
      },
    ];
  }
  if (version.bundleFiles?.length) {
    return [
      {
        assetId: `${version.assetId}:${version.version}`,
        adapter: version.adapter,
        contentMarkdown: version.contentMarkdown,
        contentHash: version.bundleRootHash ?? version.contentHash,
        targetPath: version.targetPath,
        bundle: { rootHash: version.bundleRootHash ?? version.contentHash, files: version.bundleFiles },
      },
    ];
  }
  return [
    {
      assetId: `${version.assetId}:${version.version}`,
      adapter: version.adapter,
      contentMarkdown: version.contentMarkdown,
      contentHash: version.contentHash,
      targetPath: version.targetPath,
    },
  ];
};

export interface SkillRiskFinding {
  kind: string;
  file: string;
  line: number | null;
  severity?: 'low' | 'medium' | 'high';
}

export interface SkillProposalRoleRecommendation {
  role: string;
  confidence: number;
  reason: string;
}

export interface SkillProposalTargeting {
  scope: SkillProposalScope;
  sourceEmployeeId: string | null;
  sourceEmployeeRoles: string[];
  recommendedRoles: SkillProposalRoleRecommendation[];
  selectedRoles: string[];
  roleDecisionBy: 'generator' | 'admin';
  roleDecisionAt: string | null;
}

export type SkillProposalDeliveryState = 'not_ready' | 'approved_pending_apply' | 'applied';

export interface SkillProposalDelivery {
  state: SkillProposalDeliveryState;
  lastCheckedAt: string | null;
  lastAppliedAt: string | null;
  appliedAssetId: string | null;
  appliedSkillId: string | null;
  checkEventCount: number;
  appliedEventCount: number;
}

export interface SkillProposalDetailResponse {
  proposal: SkillProposalRow;
  asset: SkillProposalAssetRow | null;
  /** New bundle-aware API fields. `asset` remains the legacy single-file fallback. */
  assets?: SkillProposalAssetRow[];
  targeting: SkillProposalTargeting;
  delivery: SkillProposalDelivery;
  reviewActions: SkillProposalReviewAction[];
}

export const proposalReviewMetadata = (detail: SkillProposalDetailResponse) => {
  const assets = detail.assets?.length ? detail.assets : detail.asset ? [detail.asset] : [];
  const adapters = [...new Set(assets.map((asset) => asset.adapter))];
  const rootHashes = [
    ...new Set(
      assets.flatMap((asset) => {
        const rootHash = asset.bundleRootHash ?? asset.bundle?.rootHash;
        return rootHash ? [rootHash] : [];
      }),
    ),
  ];
  return {
    adapters: adapters.length
      ? adapters
      : detail.proposal.targetAdapter
          .split(',')
          .map((adapter) => adapter.trim())
          .filter(Boolean),
    rootHashes,
    sourceRoleAssetId: detail.proposal.sourceRoleAssetId,
    baseVersion: detail.proposal.baseVersion,
    baseBundleHash: detail.proposal.baseBundleHash,
    riskFindings: (detail.proposal.riskFindings ?? []).map((finding) => ({
      ...finding,
      severity: finding.severity ?? detail.proposal.riskLevel,
    })),
  };
};

export interface SkillProposalsResponse {
  proposals: SkillProposalRow[];
}

export interface GenerateSkillProposalsResponse {
  generatedProposals: number;
  proposals: SkillProposalRow[];
}
